import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Search, ClipboardCheck, ChevronLeft, ChevronRight } from 'lucide-react';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

interface ControlTest {
  id: number;
  test_name: string;
  description: string;
  control_id: number;
  control_name: string;
  method: string;
  test_date: string;
  next_test_date: string;
  result: string;
  tester: string;
  tester_name: string;
  findings: string;
  evidence_ref: string;
  created_at: string;
}

interface DashboardStats {
  total: number;
  pass: number;
  fail: number;
  partial: number;
  not_tested: number;
  overdue: number;
  tests_this_month: number;
}

const resultColors: Record<string, string> = {
  pass: 'bg-green-100 text-green-800',
  fail: 'bg-red-100 text-red-800',
  partial: 'bg-yellow-100 text-yellow-800',
  not_tested: 'bg-gray-100 text-gray-800',
};

const METHODS = ['walkthrough', 'inspection', 'reperformance', 'inquiry', 'observation', 'automated', 'sampling'];
const RESULTS = ['pass', 'fail', 'partial', 'not_tested'];

const EMPTY_FORM = {
  test_name: '',
  description: '',
  control_id: '',
  method: 'walkthrough',
  test_date: '',
  next_test_date: '',
};

const EMPTY_RESULT = {
  result: 'pass',
  findings: '',
  evidence_ref: '',
};

const ControlTests: React.FC = () => {
  const [tests, setTests] = useState<ControlTest[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [createModal, setCreateModal] = useState(false);
  const [resultModal, setResultModal] = useState<ControlTest | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [resultForm, setResultForm] = useState(EMPTY_RESULT);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (search) params.set('search', search);
      if (resultFilter) params.set('result', resultFilter);
      if (methodFilter) params.set('method', methodFilter);

      const [listRes, statsRes] = await Promise.all([
        fetch(`/api/compliance/control-tests?${params}`, { headers: authHeaders() }).then(r => r.json()),
        fetch('/api/compliance/control-tests/dashboard', { headers: authHeaders() }).then(r => r.json()),
      ]);
      setTests(listRes.tests || []);
      setTotalPages(listRes.totalPages || 1);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [page, search, resultFilter, methodFilter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.test_name.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/control-tests', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...form, control_id: form.control_id ? Number(form.control_id) : undefined }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to create test');
      setCreateModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordResult = async () => {
    if (!resultModal) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/compliance/control-tests/${resultModal.id}/result`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(resultForm),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to record result');
      setResultModal(null);
      setResultForm(EMPTY_RESULT);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardCheck className="w-7 h-7 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Control Testing</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => { setForm(EMPTY_FORM); setCreateModal(true); }} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> New Test
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[
            { label: 'Total Tests', value: stats.total, color: 'text-gray-900' },
            { label: 'Pass', value: stats.pass, color: 'text-green-600' },
            { label: 'Fail', value: stats.fail, color: 'text-red-600' },
            { label: 'Partial', value: stats.partial, color: 'text-yellow-600' },
            { label: 'Overdue', value: stats.overdue, color: 'text-red-700' },
            { label: 'This Month', value: stats.tests_this_month, color: 'text-blue-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search tests..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="border rounded-lg pl-9 pr-3 py-2 text-sm w-full" />
        </div>
        <select value={resultFilter} onChange={e => { setResultFilter(e.target.value); setPage(1); }} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">All Results</option>
          {RESULTS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={methodFilter} onChange={e => { setMethodFilter(e.target.value); setPage(1); }} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">All Methods</option>
          {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : tests.length === 0 ? (
          <div className="py-12 text-gray-500">No control tests found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Test Name', 'Control', 'Method', 'Test Date', 'Next Test', 'Result', 'Tester', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {tests.map(t => {
                const overdue = t.next_test_date && new Date(t.next_test_date) < new Date() && t.result !== 'pass';
                return (
                  <tr key={t.id} className={`hover:bg-gray-50 ${overdue ? 'bg-red-50/50' : ''}`}>
                    <td className="px-4 py-3 text-left font-medium text-gray-900">{t.test_name}</td>
                    <td className="px-4 py-3 text-left text-gray-600">{t.control_name || '—'}</td>
                    <td className="px-4 py-3 text-left capitalize text-gray-600">{t.method}</td>
                    <td className="px-4 py-3 text-left text-gray-600">{t.test_date ? new Date(t.test_date).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 text-left text-gray-600">
                      {t.next_test_date ? new Date(t.next_test_date).toLocaleDateString() : '—'}
                      {overdue && <span className="ml-1 text-xs text-red-600 font-medium">OVERDUE</span>}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${resultColors[t.result] || 'bg-gray-100 text-gray-700'}`}>
                        {t.result?.replace(/_/g, ' ') || 'not tested'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left text-gray-600">{t.tester_name || t.tester || '—'}</td>
                    <td className="px-4 py-3 text-left">
                      <button onClick={() => { setResultModal(t); setResultForm({ result: t.result || 'pass', findings: t.findings || '', evidence_ref: t.evidence_ref || '' }); }} className="text-sm text-blue-600 hover:text-blue-800">
                        Record
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Create Test Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">New Control Test</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Name *</label>
                <input value={form.test_name} onChange={e => setForm(f => ({ ...f, test_name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Control ID</label>
                  <input value={form.control_id} onChange={e => setForm(f => ({ ...f, control_id: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Numeric ID" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                  <select value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Test Date</label>
                  <input type="date" value={form.test_date} onChange={e => setForm(f => ({ ...f, test_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Next Test Date</label>
                  <input type="date" value={form.next_test_date} onChange={e => setForm(f => ({ ...f, next_test_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreate} disabled={submitting || !form.test_name.trim()} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create Test'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Record Result Modal */}
      {resultModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Record Test Result</h2>
              <p className="text-sm text-gray-500">{resultModal.test_name}</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Result</label>
                <select value={resultForm.result} onChange={e => setResultForm(f => ({ ...f, result: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                  {RESULTS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Findings</label>
                <textarea value={resultForm.findings} onChange={e => setResultForm(f => ({ ...f, findings: e.target.value }))} rows={4} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Describe test findings..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Evidence Reference</label>
                <input value={resultForm.evidence_ref} onChange={e => setResultForm(f => ({ ...f, evidence_ref: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. EVD-2026-042" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setResultModal(null)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleRecordResult} disabled={submitting} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Saving...' : 'Save Result'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlTests;
